import { useMemo } from 'react';
import { SelectedDataType } from '~/_shared/components/customizeMetrics/selectedDataMetric.type';
import { type SpreadsheetColumn } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { uniqueValues } from '~/_shared/utils/array/array.helpers';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type BoundaryGroup } from '~/boundary/boundary.types';
import { useGetBoundaryFillInfo } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryFillInfo';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { useBTGsGroupedByBoundaryGroupSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  type BoundaryTerritoryGroup, type BoundaryTerritoryGroupSettings,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useSpreadsheetColumns } from '~/store/matchupData/matchupDataSelectors.hook';
import { matchBoundaryGroupDemographic } from './demographicsMetric.factory';
import { type MetricModel } from './metrics.factory';
import { areMetricsTheSame } from './metrics.helpers';
import { MetricsDataAction } from './metrics.types';
import { type VirtualMetricInferenceSourceInfo } from './metrics.types';

export const useBoundaryMetrics = (
  activeBoundaryTerritoryGroup: BoundaryTerritoryGroup | undefined,
) => {
  const hideFillMetrics = activeBoundaryTerritoryGroup?.settings?.hideFillMetrics;
  const boundaryGroups = useBoundaryGroupsSelector();
  const spreadsheetColumns = useSpreadsheetColumns();
  const allFillGroupedByBoundaryId = useBTGsGroupedByBoundaryGroupSelector().all;
  const getBoundaryFillInfo = useGetBoundaryFillInfo();

  const allFillBTGs = useMemo(() => {
    const boundaryTerritoryGroups = allFillGroupedByBoundaryId
      .find((group) => group.boundaryGroupId === activeBoundaryTerritoryGroup?.boundaryGroupId)
      ?.boundaryTerritoryGroups ?? [];

    return boundaryTerritoryGroups
      .map((btg, index) => {
        if (!btg) {
          return null;
        }
        return {
          btg,
          fillInfo: getBoundaryFillInfo(btg, index),
        };
      })
      .filter(notNullsy);
  }, [allFillGroupedByBoundaryId, activeBoundaryTerritoryGroup?.boundaryGroupId, getBoundaryFillInfo]);

  const boundaryGroup = useMemo(() => boundaryGroups.find(g => g.id === activeBoundaryTerritoryGroup?.boundaryGroupId),
    [boundaryGroups, activeBoundaryTerritoryGroup?.boundaryGroupId]);

  const selectedMetrics: ReadonlyArray<MetricModel> = useMemo(() => {
    if (!allFillBTGs || !boundaryGroup || !activeBoundaryTerritoryGroup) {
      return [];
    }

    const savedMetrics = activeBoundaryTerritoryGroup.settings.metrics
      .map(metric => getBestDemographic(metric, boundaryGroup));

    const metricsFromFills: MetricModel[] = allFillBTGs
      .flatMap(({ btg, fillInfo }) => {
        const inferenceSource: VirtualMetricInferenceSourceInfo = {
          type: 'FillConfiguration',
          inferenceSources: [{
            fillName: fillInfo.fillName,
            fillOrderNumber: fillInfo.fillOrderNumber,
            fillColor: fillInfo.backgroundColor,
            textColor: fillInfo.foregroundColor,
            btg,
          }],
        };

        return [
          ...getFillDemographics(btg.settings, boundaryGroup, inferenceSource),
          ...getFillNumericMetrics(btg.settings, spreadsheetColumns, inferenceSource),
          ...getFillGroupMetrics(btg.settings, spreadsheetColumns, inferenceSource),
        ];
      })
      .reduce((filteredMetrics, metric) => {
        const existingMetric = filteredMetrics.find(filteredMetric => areMetricsTheSame(filteredMetric, metric));
        if (existingMetric?.inferenceSource?.type === 'FillConfiguration' && metric.inferenceSource?.type === 'FillConfiguration') {
          existingMetric.inferenceSource = {
            ...existingMetric.inferenceSource,
            inferenceSources: uniqueValues([
              ...existingMetric.inferenceSource.inferenceSources,
              ...metric.inferenceSource.inferenceSources,
            ]),
          };
        }
        else {
          filteredMetrics.push(metric);
        }

        return filteredMetrics;
      }, [] as MetricModel[])
      .filter(fillMetric => !savedMetrics.some(savedMetric => areMetricsTheSame(fillMetric, savedMetric)));

    return [
      ...(hideFillMetrics ? [] : metricsFromFills),
      ...savedMetrics,
    ];
  }, [allFillBTGs, boundaryGroup, activeBoundaryTerritoryGroup, hideFillMetrics, spreadsheetColumns]);

  return selectedMetrics;
};

type BoundarySettingsSubset = Pick<BoundaryTerritoryGroupSettings, 'boundaryTerritoryType' | 'bucketColumnId' | 'demographicId'>;

const getFillDemographics = (
  btgSettings: BoundarySettingsSubset,
  boundaryGroup: BoundaryGroup,
  inferenceSource: VirtualMetricInferenceSourceInfo,
): ReadonlyArray<MetricModel> => {
  if (btgSettings.boundaryTerritoryType !== BoundaryTerritoryType.Demographic) {
    return [];
  }

  const demographicFromFill = boundaryGroup.demographics
    .find(d => d.id === btgSettings.demographicId);

  if (demographicFromFill) {
    return [{
      type: SelectedDataType.Demographic,
      demographic: demographicFromFill,
      inferenceSource,
    } as const];
  }

  return [];
};

const getFillNumericMetrics = (
  btgSettings: BoundarySettingsSubset,
  spreadsheetColumns: ReadonlyArray<SpreadsheetColumn>,
  inferenceSource: VirtualMetricInferenceSourceInfo,
): ReadonlyArray<MetricModel> => {
  if (btgSettings.boundaryTerritoryType !== BoundaryTerritoryType.Numeric) {
    return [];
  }

  const spreadsheetColumn = spreadsheetColumns.find(c => c.id === btgSettings.bucketColumnId);
  if (!spreadsheetColumn) {
    return [];
  }

  const numericActions = [MetricsDataAction.SUM, MetricsDataAction.AVERAGE, MetricsDataAction.HIGH_VALUE,
    MetricsDataAction.LOW_VALUE] as const;
  return numericActions.map(dataAction => ({
    type: SelectedDataType.SpreadsheetColumn,
    spreadsheetColumn,
    dataAction,
    inferenceSource,
  } as const));
};

const getFillGroupMetrics = (
  btgSettings: BoundarySettingsSubset,
  spreadsheetColumns: ReadonlyArray<SpreadsheetColumn>,
  inferenceSource: VirtualMetricInferenceSourceInfo,
): ReadonlyArray<MetricModel> => {
  if (btgSettings.boundaryTerritoryType !== BoundaryTerritoryType.Groups) {
    return [];
  }

  const spreadsheetColumn = spreadsheetColumns.find(c => c.id === btgSettings.bucketColumnId);
  if (!spreadsheetColumn) {
    return [];
  }

  return [{
    type: SelectedDataType.SpreadsheetColumn,
    spreadsheetColumn,
    dataAction: MetricsDataAction.GROUP_COUNT,
    inferenceSource,
  } as const];
};

const getBestDemographic = (metric: MetricModel, boundaryGroup: BoundaryGroup) => {
  if (metric.type === SelectedDataType.Demographic) {
    const nativeDemographic = boundaryGroup.demographics
      .find(demographic => matchBoundaryGroupDemographic(demographic.id, metric.demographic.id));

    if (nativeDemographic) {
      return {
        ...metric,
        demographic: nativeDemographic,
      };
    }
  }

  return metric;
};
